import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, RefreshCw, Clock, Package, Loader2, User,
} from "lucide-react";

/**
 * AdminMembership — approval console for pending Business Starter Kit
 * purchases. Admin sees the receipt image + reference number, cross-checks
 * against GCash/Maya history, then approves or rejects. Approve activates
 * the partner's membership (unlocking withdrawals); reject flips them back
 * to inactive so they can retry with a corrected receipt.
 */
export default function AdminMembership() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [zoomImg, setZoomImg] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/membership/pending");
      setRows(data || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (m) => {
    if (!window.confirm(`Approve ${m.user_email}'s ${m.tier_name} kit purchase (₱${m.amount_paid.toLocaleString()})?\n\nThis will activate their membership and unlock withdrawals.`)) return;
    setBusyId(m.id);
    try {
      await api.post(`/admin/membership/${m.id}/approve`);
      toast.success(`${m.user_email} activated as ${m.tier_name}.`);
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyId(null); }
  };

  const doReject = async () => {
    if (!rejectFor) return;
    setBusyId(rejectFor.id);
    try {
      await api.post(`/admin/membership/${rejectFor.id}/reject`, null, {
        params: { reason: rejectReason.trim() || "Receipt does not match records" },
      });
      toast.success(`${rejectFor.user_email} rejected. Reset to inactive.`);
      setRejectFor(null); setRejectReason("");
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-5" data-testid="admin-membership-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-[#FBBF24] font-bold">Kit Approvals</div>
          <h2 className="font-display text-2xl font-black">Pending Business Starter Kit Purchases</h2>
          <p className="text-white/60 text-sm mt-1">Verify each receipt against GCash/Maya inbox before approving.</p>
        </div>
        <button
          onClick={load}
          className="text-xs px-3 py-2 border border-white/15 rounded hover:border-[#FBBF24] hover:text-[#FBBF24] inline-flex items-center gap-1"
          data-testid="admin-membership-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="glass-card p-6 text-white/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</div>
      ) : rows.length === 0 ? (
        <div className="glass-card p-10 text-center text-white/50" data-testid="admin-membership-empty">
          <Clock size={32} className="mx-auto opacity-40" />
          <div className="mt-3">No pending kit purchases. All caught up.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((m) => (
            <div key={m.id} className="glass-card p-5" data-testid={`kit-row-${m.id}`}>
              <div className="grid gap-4 md:grid-cols-[240px_1fr_auto] items-start">
                <button
                  onClick={() => setZoomImg(m.receipt_image)}
                  className="relative w-full h-40 rounded-md overflow-hidden bg-black/40 border border-white/10 hover:border-[#FBBF24]"
                  data-testid={`kit-receipt-${m.id}`}
                  title="Click to enlarge"
                >
                  <img src={m.receipt_image} alt="Receipt" className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-[10px] uppercase tracking-widest text-white text-center py-0.5">
                    Click to enlarge
                  </div>
                </button>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-[#FBBF24]/15 text-[#FBBF24] border border-[#FBBF24]/40">
                      <Package size={11} /> {m.tier_name}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/5 text-white/70 border border-white/10">
                      {m.channel}
                    </span>
                    <span className="text-[10px] uppercase tracking-widest text-white/40">Filed {m.submitted_at?.slice(0, 16)?.replace("T", " ")}</span>
                  </div>
                  <div className="flex items-center gap-2 text-white">
                    <User size={13} className="text-[#FBBF24]" />
                    <span className="font-display font-black">{m.user_name}</span>
                    <span className="text-white/50 text-sm">· {m.user_email}</span>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-3 mt-2">
                    <Field label="Amount paid" value={`₱${m.amount_paid.toLocaleString(undefined, { minimumFractionDigits: 2 })}`} highlight />
                    <Field label="Reference" value={<span className="font-mono-num">{m.reference_number}</span>} />
                    <Field label="Sender" value={m.sender_name} />
                  </div>
                </div>
                <div className="flex flex-col gap-2 min-w-[140px]">
                  <button
                    onClick={() => approve(m)}
                    disabled={busyId === m.id}
                    className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-widest bg-emerald-500/15 border border-emerald-300/40 text-emerald-300 hover:bg-emerald-500 hover:text-white inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid={`kit-approve-${m.id}`}
                  >
                    <CheckCircle2 size={13} /> Approve
                  </button>
                  <button
                    onClick={() => { setRejectFor(m); setRejectReason(""); }}
                    disabled={busyId === m.id}
                    className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-widest bg-[#DC2626]/15 border border-[#DC2626]/40 text-[#FCA5A5] hover:bg-[#DC2626] hover:text-white inline-flex items-center justify-center gap-1 disabled:opacity-50"
                    data-testid={`kit-reject-${m.id}`}
                  >
                    <XCircle size={13} /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Receipt zoom modal */}
      {zoomImg && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4" onClick={() => setZoomImg(null)} data-testid="receipt-zoom-modal">
          <img src={zoomImg} alt="Receipt" className="max-w-full max-h-[92vh] rounded" />
        </div>
      )}

      {/* Reject reason modal */}
      {rejectFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="reject-modal">
          <div className="glass-card p-6 max-w-md w-full">
            <div className="font-display text-lg font-black">Reject kit purchase?</div>
            <div className="text-sm text-white/70 mt-1">
              {rejectFor.user_email} · {rejectFor.tier_name} · ₱{rejectFor.amount_paid.toLocaleString()}
            </div>
            <label className="zfd-label mt-4">Reason (optional — shown to partner)</label>
            <textarea
              rows={3}
              className="zfd-input"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Receipt reference not found in GCash inbox."
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setRejectFor(null); setRejectReason(""); }}
                className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-widest bg-white/5 border border-white/10 text-white/70 hover:border-white/30"
                data-testid="reject-cancel"
              >
                Cancel
              </button>
              <button
                onClick={doReject}
                disabled={busyId === rejectFor.id}
                className="px-3 py-2 rounded-md text-xs font-bold uppercase tracking-widest bg-[#DC2626] text-white hover:bg-red-500 disabled:opacity-50 inline-flex items-center gap-1"
                data-testid="reject-confirm"
              >
                <XCircle size={13} /> Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 font-bold">{label}</div>
      <div className={highlight ? "text-[#FBBF24] font-mono-num font-bold text-lg" : "text-white text-sm"}>{value}</div>
    </div>
  );
}
